const ROOT_OVERFLOW_PX_THRESHOLD = 1;
const ZOOM_SCALE_THRESHOLD = 1.01;
const DELTA_X_EPSILON = 0.01;

const isFiniteNumber = (value) =>
  typeof value === "number" && Number.isFinite(value);

export const isNodeHorizontallyScrollable = (
  node,
  {
    getComputedStyle,
    minOverflowPx = ROOT_OVERFLOW_PX_THRESHOLD,
    allowedOverflowValues = ["auto", "scroll"],
  } = {}
) => {
  if (!node || typeof getComputedStyle !== "function") return false;

  const style = getComputedStyle(node);
  const overflowX = style?.overflowX;
  if (!allowedOverflowValues.includes(overflowX)) return false;

  const scrollWidth = Number(node.scrollWidth);
  const clientWidth = Number(node.clientWidth);
  if (!isFiniteNumber(scrollWidth) || !isFiniteNumber(clientWidth)) return false;

  return scrollWidth > clientWidth + minOverflowPx;
};

export const hasScrollableHorizontalAncestor = (
  target,
  { isNodeScrollable, rootElements = [] } = {}
) => {
  if (!target || typeof isNodeScrollable !== "function") return false;

  const roots = new Set(rootElements.filter(Boolean));
  let current =
    target?.nodeType === 3 ? (target.parentElement ?? target.parentNode) : target;

  while (current) {
    if (!roots.has(current) && isNodeScrollable(current)) {
      return true;
    }
    const next = current.parentElement ?? current.parentNode ?? null;
    if (!next || next === current) break;
    current = next;
  }

  return false;
};

export const isViewportZoomed = (
  visualViewport,
  { zoomScaleThreshold = ZOOM_SCALE_THRESHOLD } = {}
) => {
  const scale = Number(visualViewport?.scale);
  if (!isFiniteNumber(scale)) return false;
  return scale > zoomScaleThreshold;
};

export const canDocumentScrollHorizontally = (
  doc,
  { minOverflowPx = ROOT_OVERFLOW_PX_THRESHOLD } = {}
) => {
  if (!doc) return false;

  const candidates = [doc.documentElement, doc.body].filter(Boolean);
  return candidates.some((node) => {
    const scrollWidth = Number(node.scrollWidth);
    const clientWidth = Number(node.clientWidth);
    return (
      isFiniteNumber(scrollWidth) &&
      isFiniteNumber(clientWidth) &&
      scrollWidth > clientWidth + minOverflowPx
    );
  });
};

export const shouldPreventHorizontalWheelNavigation = (
  event,
  {
    hasScrollableAncestor = false,
    viewportZoomed = false,
    pageCanScrollHorizontally = false,
    deltaXEpsilon = DELTA_X_EPSILON,
  } = {}
) => {
  if (!event || event.defaultPrevented || event.cancelable === false) return false;

  const resolveBool = (valueOrResolver) => {
    if (typeof valueOrResolver === "function") {
      return !!valueOrResolver();
    }
    return !!valueOrResolver;
  };

  const absDeltaX = Math.abs(Number(event.deltaX) || 0);
  const absDeltaY = Math.abs(Number(event.deltaY) || 0);

  if (absDeltaX <= deltaXEpsilon) return false;
  if (absDeltaX <= absDeltaY) return false;
  if (resolveBool(hasScrollableAncestor)) return false;
  if (resolveBool(viewportZoomed)) return false;
  if (resolveBool(pageCanScrollHorizontally)) return false;

  return true;
};
